import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import Card from "@mui/material/Card";
import { analyticsClient, basicAPI, client, docstoreClient } from "api";
import { setGlobalState, useGlobalState } from "Global";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import BasicLayout from "../components/BasicLayout";
import bgImage from "assets/images/background.png";
import {
  PINGFED_PROMPT_NONE_KEY,
  consumePingfedReturnTo,
  passwordSignInNavigateArgs,
} from "auth/authConfig";

const isSilentRenewIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export default function PingFederateCallback() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [userDetails] = useGlobalState("userDetails");
  const inSilentIframe = isSilentRenewIframe();

  useEffect(() => {
    // Silent renew reuses this callback URI in a hidden iframe.
    // AuthProvider finishes signinSilentCallback; do not run interactive login here.
    if (inSilentIframe || auth.isLoading) return;

    if (auth.error) {
      const wasPromptNone = sessionStorage.getItem(PINGFED_PROMPT_NONE_KEY);

      // Expected when there is no PingFed SSO session — go to sign-in quietly
      if (!wasPromptNone) {
        setGlobalState("error", {
          open: true,
          message:
            "PingFederate sign-in is unavailable. Please log in with your username and password.",
          type: "error",
        });
      }

      // Drop broken OIDC state, then fall back to username/password sign-in.
      void auth.removeUser?.().finally(() => {
        navigate("/authentication/sign-in", passwordSignInNavigateArgs());
      });
      return;
    }

    if (auth.isAuthenticated && auth.user) {
      sessionStorage.removeItem(PINGFED_PROMPT_NONE_KEY);
      const token = auth.user.access_token;
      const returnTo = consumePingfedReturnTo("/");

      client.defaults.headers.common.Authorization = `Bearer ${token}`;
      docstoreClient.defaults.headers.common.Authorization = `Bearer ${token}`;
      analyticsClient.defaults.headers.common.Authorization = `Bearer ${token}`;

      const hasExistingUserDetails =
        userDetails &&
        Object.keys(userDetails).length > 0 &&
        (userDetails.name || userDetails.access_token);

      if (!hasExistingUserDetails) {
        basicAPI
          .refreshToken()
          .then((res) => {
            setGlobalState("userDetails", {
              ...res.data,
              access_token: token,
            });
            navigate(returnTo, { replace: true });
          })
          .catch(() => {
            setGlobalState("error", {
              open: true,
              message:
                "PingFederate sign-in is unavailable. Please log in with your username and password.",
              type: "error",
            });
            void auth.removeUser?.().finally(() => {
              navigate("/authentication/sign-in", passwordSignInNavigateArgs());
            });
          });
      } else {
        setGlobalState("userDetails", (prevState) => ({
          ...prevState,
          access_token: token,
        }));
        navigate(returnTo, { replace: true });
      }
    }
  }, [
    auth.error,
    auth.isAuthenticated,
    auth.isLoading,
    auth.removeUser,
    auth.user,
    inSilentIframe,
    navigate,
    userDetails,
  ]);

  if (inSilentIframe) {
    return null;
  }

  return (
    <BasicLayout image={bgImage}>
      <Card sx={{ minWidth: "2rem" }}>
        <MDBox px={3} py={3}>
          <MDBox sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <MDBox
              sx={{
                width: "26px",
                height: "26px",
                borderRadius: "50%",
                border: "3px solid #e2e8f0",
                borderTopColor: "#2563eb",
                animation: "pf-spin 0.9s linear infinite",
              }}
            />
            <MDTypography variant="h5" fontWeight="bold" color="dark">
              {auth.isLoading ? "Verifying Token" : "Fetching User Details"}
            </MDTypography>
          </MDBox>

          <MDTypography variant="button" color="text" sx={{ display: "block", lineHeight: 1.7, mb: 2 }}>
            Completing PingFederate login and securely validating your session. This usually takes a few
            seconds.
          </MDTypography>

          <MDBox
            sx={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              px: 1.5,
              py: 1.25,
            }}
          >
            <MDTypography variant="caption" color="dark">
              Please keep this tab open. You will be redirected automatically.
            </MDTypography>
          </MDBox>

          <style>
            {`
              @keyframes pf-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}
